import { useState, useCallback, useEffect, useRef } from 'react';
import addressService from '../services/addressService';
import { maskZipCode } from '../utils/masks';
import { isValidZipCode } from '../utils/validation';

/**
 * Hook para busca e gerenciamento de endereço
 * @param {Object} initialAddress - Endereço inicial
 * @param {string|number} customerId - ID do cliente (para empresa de transporte)
 * @returns {Object} Objeto com address, addressLocked, isAddressModified, handleAddressChange, fetchAddressByZipCode, fetchAddressById, unlockAddress, setAddress
 */
export function useAddress(initialAddress = {}, customerId = null) {
    const [address, setAddress] = useState({
        zipCode: initialAddress.zipCode || '',
        street: initialAddress.street || '',
        number: initialAddress.number || '',
        complement: initialAddress.complement || '',
        district: initialAddress.district || '',
        city: initialAddress.city || '',
        stateAbbr: initialAddress.stateAbbr || '',
        stateName: initialAddress.stateName || '',
        addressId: initialAddress.addressId || null,
    });

    // addressLocked: true quando endereço foi carregado automaticamente (não modificado pelo usuário)
    // false quando usuário pode editar manualmente
    const [addressLocked, setAddressLocked] = useState(!!initialAddress.addressId);
    const [isAddressModified, setIsAddressModified] = useState(false);
    const [loadingAddress, setLoadingAddress] = useState(false);
    const [lastFetchedZipCode, setLastFetchedZipCode] = useState(initialAddress.zipCode || '');

    // Ref para rastrear o último initialAddress processado e evitar loops
    const lastInitialAddressRef = useRef(null);
    const isInitialMount = useRef(true);
    
    // Atualiza o endereço quando initialAddress muda externamente (ex: quando dados são carregados na edição)
    useEffect(() => {
        // Ignora na primeira renderização
        if (isInitialMount.current) {
            isInitialMount.current = false;
            lastInitialAddressRef.current = initialAddress;
            return;
        }
        
        // Compara por referência para evitar loops
        if (lastInitialAddressRef.current === initialAddress) {
            return;
        }
        
        if (initialAddress && (
            initialAddress.zipCode || 
            initialAddress.street || 
            initialAddress.addressId ||
            initialAddress.number ||
            initialAddress.district ||
            initialAddress.city ||
            initialAddress.stateAbbr
        )) {
            setAddress(prev => {
                // Só atualiza se houver mudanças significativas
                const hasChanges = 
                    (initialAddress.zipCode && initialAddress.zipCode !== prev.zipCode) ||
                    (initialAddress.street && initialAddress.street !== prev.street) ||
                    (initialAddress.addressId && initialAddress.addressId !== prev.addressId) ||
                    (initialAddress.number && initialAddress.number !== prev.number) ||
                    (initialAddress.complement !== undefined && initialAddress.complement !== prev.complement) ||
                    (initialAddress.district && initialAddress.district !== prev.district) ||
                    (initialAddress.city && initialAddress.city !== prev.city) ||
                    (initialAddress.stateAbbr && initialAddress.stateAbbr !== prev.stateAbbr);
                
                if (!hasChanges) {
                    lastInitialAddressRef.current = initialAddress;
                    return prev;
                }
                
                const newAddress = {
                    zipCode: initialAddress.zipCode !== undefined ? initialAddress.zipCode : prev.zipCode,
                    street: initialAddress.street !== undefined ? initialAddress.street : prev.street,
                    number: initialAddress.number !== undefined ? initialAddress.number : prev.number,
                    complement: initialAddress.complement !== undefined ? initialAddress.complement : prev.complement,
                    district: initialAddress.district !== undefined ? initialAddress.district : prev.district,
                    city: initialAddress.city !== undefined ? initialAddress.city : prev.city,
                    stateAbbr: initialAddress.stateAbbr !== undefined ? initialAddress.stateAbbr : prev.stateAbbr,
                    stateName: initialAddress.stateName !== undefined ? initialAddress.stateName : prev.stateName,
                    addressId: initialAddress.addressId !== undefined ? initialAddress.addressId : prev.addressId,
                };
                
                lastInitialAddressRef.current = initialAddress;
                return newAddress;
            });
            
            // Se tem addressId, significa que foi carregado do banco (edição)
            if (initialAddress.addressId) {
                setAddressLocked(true);
                setIsAddressModified(false);
                setLastFetchedZipCode(initialAddress.zipCode || '');
            }
        } else {
            lastInitialAddressRef.current = initialAddress;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- omit initialAddress completo para evitar loops; campos listados cobrem mudanças relevantes
    }, [
        initialAddress?.zipCode, 
        initialAddress?.street, 
        initialAddress?.addressId,
        initialAddress?.number,
        initialAddress?.district,
        initialAddress?.city,
        initialAddress?.stateAbbr
    ]);

    /**
     * Busca endereço por CEP
     * @param {string} zipCode - CEP (pode estar mascarado)
     */
    const fetchAddressByZipCode = useCallback(async (zipCode) => {
        const cleanZip = zipCode.replace(/\D/g, '');
        if (cleanZip.length !== 8) return;

        setLoadingAddress(true);
        try {
            const data = await addressService.getByZipCode(cleanZip, customerId);
            setAddress(prev => ({
                ...prev,
                zipCode: zipCode, // Mantém o CEP digitado pelo usuário (com máscara)
                street: data.street || '',
                district: data.district || '',
                city: data.city || '',
                stateAbbr: data.state?.abbreviation || '',
                stateName: data.state?.name || '',
                addressId: data.id || null,
            }));
            setAddressLocked(true);
            setIsAddressModified(false);
            setLastFetchedZipCode(zipCode);
        } catch (error) {
            setAddressLocked(false);
            setIsAddressModified(true);
            // Limpa campos para permitir entrada manual
            setAddress(prev => ({
                ...prev,
                street: '',
                district: '',
                city: '',
                stateAbbr: '',
                stateName: '',
                addressId: null,
            }));
        } finally {
            setLoadingAddress(false);
        }
    }, [customerId]);

    /**
     * Busca endereço por ID
     * @param {string|number} addressId - ID do endereço
     */
    const fetchAddressById = useCallback(async (addressId) => {
        if (!addressId) return;

        setLoadingAddress(true);
        try {
            const data = await addressService.getById(addressId, customerId);
            setAddress({
                zipCode: data.zipCode || '',
                street: data.street || '',
                number: data.number || '',
                complement: data.complement || '',
                district: data.district || '',
                city: data.city || '',
                stateAbbr: data.state?.abbreviation || '',
                stateName: data.state?.name || '',
                addressId: data.id || null,
            });
            setAddressLocked(true);
            setIsAddressModified(false);
            setLastFetchedZipCode(data.zipCode || '');
        } catch (error) {
            setAddressLocked(false);
            setIsAddressModified(true);
        } finally {
            setLoadingAddress(false);
        }
    }, [customerId]);

    /**
     * Handler para mudanças em campos de endereço
     * @param {Event|Object} e - Event do input ou objeto { name, value }
     */
    const handleAddressChange = useCallback((e) => {
        const name = e.target?.name || e.name;
        let value = e.target?.value !== undefined ? e.target.value : e.value;

        // Aplica máscara no CEP
        if (name === 'zipCode') {
            value = maskZipCode(value);
            
            // Se o CEP foi alterado manualmente (diferente do último buscado), desbloqueia campos
            if (value !== lastFetchedZipCode && addressLocked) {
                setAddressLocked(false);
                setIsAddressModified(true);
                // Limpa campos de endereço quando CEP muda
                setAddress(prev => ({
                    ...prev,
                    zipCode: value,
                    street: '',
                    district: '',
                    city: '',
                    stateAbbr: '',
                    stateName: '',
                    addressId: null,
                }));
            } else {
                // Atualiza apenas o CEP
                setAddress(prev => ({
                    ...prev,
                    zipCode: value,
                }));
            }
            
            // Busca endereço quando CEP estiver completo e válido
            if (value.length === 9 && isValidZipCode(value)) {
                fetchAddressByZipCode(value);
            }
            
            return; // Retorna aqui para não atualizar o endereço novamente abaixo
        }

        // Para outros campos, marca como modificado se for campo bloqueado
        const addressFields = ['street', 'complement', 'district', 'city', 'stateAbbr'];
        if (addressFields.includes(name)) {
            if (addressLocked) {
                setAddressLocked(false);
                setIsAddressModified(true);
            }
        }

        setAddress(prev => ({
            ...prev,
            [name]: value,
        }));
    }, [fetchAddressByZipCode, addressLocked, lastFetchedZipCode]);

    /**
     * Desbloqueia campos de endereço para edição manual
     */
    const unlockAddress = useCallback(() => {
        setAddressLocked(false);
        setIsAddressModified(true);
    }, []);

    /**
     * Define o endereço completo
     * @param {Object} newAddress - Novo endereço
     */
    const setAddressData = useCallback((newAddress) => {
        if (!newAddress) return;
        
        setAddress(prev => {
            // Verifica se realmente mudou para evitar loops
            const hasChanged = 
                (newAddress.zipCode !== undefined && newAddress.zipCode !== prev.zipCode) ||
                (newAddress.street !== undefined && newAddress.street !== prev.street) ||
                (newAddress.addressId !== undefined && newAddress.addressId !== prev.addressId) ||
                (newAddress.number !== undefined && newAddress.number !== prev.number) ||
                (newAddress.complement !== undefined && newAddress.complement !== prev.complement) ||
                (newAddress.district !== undefined && newAddress.district !== prev.district) ||
                (newAddress.city !== undefined && newAddress.city !== prev.city) ||
                (newAddress.stateAbbr !== undefined && newAddress.stateAbbr !== prev.stateAbbr);
            
            if (!hasChanged) return prev;
            
            return {
                ...prev,
                ...newAddress,
            };
        });
        
        if (newAddress.addressId) {
            setAddressLocked(true);
            setIsAddressModified(false);
            setLastFetchedZipCode(newAddress.zipCode || '');
        } else if (newAddress.addressId === null || newAddress.addressId === undefined) {
            // Só atualiza se não tem addressId e realmente precisa mudar
            setAddressLocked(false);
            setIsAddressModified(true);
        }
    }, []);

    /**
     * Reseta o endereço
     */
    const resetAddress = useCallback(() => {
        setAddress({
            zipCode: '',
            street: '',
            number: '',
            complement: '',
            district: '',
            city: '',
            stateAbbr: '',
            stateName: '',
            addressId: null,
        });
        setAddressLocked(false);
        setIsAddressModified(false);
        setLastFetchedZipCode('');
    }, []);

    return {
        address,
        setAddress: setAddressData,
        addressLocked,
        isAddressModified,
        loadingAddress,
        handleAddressChange,
        fetchAddressByZipCode,
        fetchAddressById,
        unlockAddress,
        resetAddress,
    };
}
